// components
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import Chart, { useChart } from 'src/components/chart';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent, formatNumber } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
// ----------------------------------------------------------------------
type Props = {
  series: {
    name: string;
    data: number[];
  }[];
  labels: string[];
  /** Override the per-series fill colors. Falls back to the legacy palette when omitted. */
  colors?: string[];
  /** Top radius applied to the last stacked segment of each column. */
  barBorderRadius?: number;
  /** Render the percentage of each segment on the bars. */
  showDataLabels?: boolean;
  /** Diameter (px) of the round legend markers. */
  legendMarkerSize?: number;
  /** Override the legend label text color. */
  legendLabelColor?: string;
  /** Chart render height (px). */
  height?: number;
  /** Hide the built-in ApexCharts legend (use when rendering a custom legend outside). */
  hideLegend?: boolean;
  /** Previous-period series used for evolution percentages below the X axis. */
  comparisonSeries?: {
    name: string;
    data: number[];
  }[];
  /** Evolution values are hidden by default and controlled by the parent's switch. */
  showEvolution?: boolean;
  /** Opt-in absolute-total rows appended below the per-series rows in the tooltip */
  extraTooltipRows?: {
    name: string;
    color: string;
    compute: (
      values: number[],
      names: string[]
    ) => number;
  }[];
};
// ----------------------------------------------------------------------
export default function ChartColumnStackedPercent({
  series,
  labels,
  colors,
  barBorderRadius = 0,
  showDataLabels = false,
  legendMarkerSize = 16,
  legendLabelColor,
  height = 350,
  hideLegend = false,
  extraTooltipRows,
  comparisonSeries = [],
  showEvolution = false,
}: Props) {
  const theme = useTheme();
  const filterStore = useSelector(
    (state: any) => state.filters
  );
  const workspace =
    useCurrentWorkspace();
  const chartRef =
    useRef(null);
  const chartContainerRef =
    useRef<HTMLDivElement | null>(
      null
    );
  const comparisonContainerRef = useRef<HTMLDivElement | null>(null);
  const comparisonItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [
    hiddenSeries,
    setHiddenSeries,
  ] = useState<Set<string>>(
    () => new Set()
  );
  const annotations =
    generateAnnotations(
      workspace,
      filterStore,
      labels
    );
  enum ColorSchema {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
    TERTIARY = 'tertiary',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info',
    SUCCESS = 'success',
  }
  const resolvedColors =
    colors ??
    (
      series?.length === 3
        ? [
          theme.palette[
            ColorSchema.SUCCESS
          ].light,
          theme.palette[
            ColorSchema.TERTIARY
          ].light,
          '#b7b7b7',
        ]
        : series?.length === 5
          ? [
            theme.palette[
              ColorSchema.SUCCESS
            ].light,
            '#1e88e5',
            '#64b5f6',
            '#b7dcfb',
            '#b7b7b7',
          ]
          : [
            '#118d57',
            '#1e88e5',
            '#64b5f6',
            '#E1E1E1',
          ]
    );
  const visibleSeriesCount = series.filter(
    (serie) => !hiddenSeries.has(serie.name)
  ).length;
  const showComparison =
    showEvolution &&
    Boolean(filterStore?.compare) &&
    comparisonSeries.length > 0;
  const comparisonValues = useMemo(
    () =>
      labels.map((_, bucketIndex) =>
        series.map((serie, seriesIndex) => {
          const currentValue =
            Number(serie.data?.[bucketIndex]) || 0;
          const compareValue =
            Number(
              comparisonSeries.find((compared) => compared.name === serie.name)?.data?.[
                bucketIndex
              ]
            ) || 0;
          let value: number | null = null;
          if (compareValue !== 0) {
            value =
              ((currentValue - compareValue) /
                Math.abs(compareValue)) *
              100;
          } else if (currentValue === 0) {
            value = 0;
          }
          return {
            name: serie.name,
            color:
              resolvedColors[seriesIndex] ||
              '#637381',
            value,
          };
        })
      ),
    [
      labels,
      series,
      comparisonSeries,
      resolvedColors,
    ]
  );
  const formatComparisonValue = (
    value: number | null
  ): string => {
    if (value === null) return '—';
    if (value > 999) return '>999%';
    if (value < -999) return '<-999%';
    return `${value > 0 ? '+' : ''}${Math.round(
      value
    )}%`;
  };
  useLayoutEffect(() => {
    if (!showComparison) {
      return undefined;
    }
    const chartContainer =
      chartContainerRef.current;
    const comparisonContainer =
      comparisonContainerRef.current;
    if (
      !chartContainer ||
      !comparisonContainer
    ) {
      return undefined;
    }
    let animationFrameId: number | null = null;
    const updateComparisonPositions = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId =
        requestAnimationFrame(() => {
          const xAxisLabels =
            chartContainer.querySelectorAll<SVGTextElement>(
              '.apexcharts-xaxis-texts-g .apexcharts-xaxis-label'
            );
          const comparisonRect =
            comparisonContainer.getBoundingClientRect();
          comparisonItemRefs.current.forEach(
            (comparisonItem, index) => {
              const xAxisLabel =
                xAxisLabels[index];
              if (
                !comparisonItem ||
                !xAxisLabel
              ) {
                return;
              }
              const labelRect =
                xAxisLabel.getBoundingClientRect();
              const labelCenterX =
                labelRect.left +
                labelRect.width / 2 -
                comparisonRect.left;
              comparisonItem.style.left =
                `${labelCenterX}px`;
            }
          );
        });
    };
    updateComparisonPositions();
    const resizeObserver =
      new ResizeObserver(
        updateComparisonPositions
      );
    resizeObserver.observe(chartContainer);
    resizeObserver.observe(
      comparisonContainer
    );
    window.addEventListener(
      'resize',
      updateComparisonPositions
    );
    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(
          animationFrameId
        );
      }
      resizeObserver.disconnect();
      window.removeEventListener(
        'resize',
        updateComparisonPositions
      );
    };
  }, [
    showComparison,
    labels,
    comparisonValues,
    hiddenSeries,
  ]);
  // --------------------------------------------------------------------
  // Custom annotation labels
  // --------------------------------------------------------------------
  const MIN_LABEL_SEGMENT_HEIGHT = 10;
  const labelAnnotations: any =
    showDataLabels
      ? series.flatMap(
        (
          serie,
          seriesIndex
        ) => {
          if (
            hiddenSeries.has(
              serie.name
            )
          ) {
            return [];
          }
          return serie.data
            .map(
              (
                value,
                dataPointIndex
              ) => {
                const numericValue =
                  Number(value);
                if (
                  !Number.isFinite(
                    numericValue
                  ) ||
                  numericValue <= 0
                ) {
                  return null;
                }
                /*
                 * Pour un stacked 100% :
                 * le Y de l'annotation doit correspondre
                 * au cumul des pourcentages des séries visibles.
                 */
                let visibleTotal = 0;
                series.forEach(
                  (
                    currentSeries
                  ) => {
                    if (
                      hiddenSeries.has(
                        currentSeries.name
                      )
                    ) {
                      return;
                    }
                    const currentValue =
                      Number(
                        currentSeries
                          .data[
                        dataPointIndex
                        ]
                      );
                    if (
                      Number.isFinite(
                        currentValue
                      ) &&
                      currentValue > 0
                    ) {
                      visibleTotal +=
                        currentValue;
                    }
                  }
                );
                if (
                  visibleTotal <= 0
                ) {
                  return null;
                }
                let cumulativeValue = 0;
                for (
                  let i = 0;
                  i <= seriesIndex;
                  i += 1
                ) {
                  const currentSeries =
                    series[i];
                  if (
                    hiddenSeries.has(
                      currentSeries.name
                    )
                  ) {
                    continue;
                  }
                  const currentValue =
                    Number(
                      currentSeries
                        .data[
                      dataPointIndex
                      ]
                    );
                  if (
                    Number.isFinite(
                      currentValue
                    ) &&
                    currentValue > 0
                  ) {
                    cumulativeValue +=
                      currentValue;
                  }
                }
                const percentage =
                  (
                    numericValue /
                    visibleTotal
                  ) * 100;
                const cumulativePercent =
                  (
                    cumulativeValue /
                    visibleTotal
                  ) * 100;
                return {
                  x:
                    labels[
                    dataPointIndex
                    ],
                  /*
                   * stackType 100% => axe 0..100
                   */
                  y:
                    cumulativePercent,
                  marker: {
                    size: 0,
                    strokeWidth: 0,
                    strokeColor: 'transparent',
                    fillColor: 'transparent',
                  },
                  label: {
                    text:
                      `${percentage.toFixed(
                        0
                      )}%`,
                    /*
                     * Comme pour tes autres charts :
                     * juste au niveau du sommet du segment.
                     */
                    offsetY: 7,
                    borderColor:
                      'rgba(145, 158, 171, 0.20)',
                    borderWidth: 1,
                    borderRadius: 4,
                    style: {
                      background:
                        '#FFFFFF',
                      color:
                        '#1C252E',
                      fontSize:
                        '11px',
                      fontWeight:
                        500,
                      fontFamily:
                        'Public Sans, sans-serif',
                      cssClass:
                        `chart-data-label chart-data-label-series-${seriesIndex} chart-data-label-point-${dataPointIndex}`,
                    },
                  },
                };
              }
            )
            .filter(Boolean);
        }
      )
      : [];
  // --------------------------------------------------------------------
  // Rebuild annotation labels like:
  //
  // display: inline-flex;
  // padding: 4px 8px;
  // justify-content: center;
  // align-items: center;
  // gap: 4px;
  // --------------------------------------------------------------------
  const injectSeriesDotsIntoLabels =
    () => {
      requestAnimationFrame(
        () => {
          const container =
            chartContainerRef.current;
          if (!container) {
            return;
          }
          container
            .querySelectorAll(
              '.chart-label-dot'
            )
            .forEach(
              (dot) =>
                dot.remove()
            );
          series.forEach(
            (
              serie,
              seriesIndex
            ) => {
              if (
                hiddenSeries.has(
                  serie.name
                )
              ) {
                return;
              }
              const seriesColor =
                resolvedColors[
                seriesIndex
                ] ||
                '#637381';
              const textElements =
                container.querySelectorAll(
                  `text.chart-data-label-series-${seriesIndex}`
                );
              textElements.forEach(
                (node) => {
                  const text =
                    node as SVGTextElement;
                  // ----------------------------------------------
                  // Find corresponding rect
                  // ----------------------------------------------
                  let rect =
                    text.previousElementSibling as SVGRectElement | null;
                  if (
                    !rect ||
                    rect.tagName.toLowerCase() !==
                    'rect'
                  ) {
                    const parent =
                      text.parentElement;
                    if (!parent) {
                      return;
                    }
                    const siblings =
                      Array.from(
                        parent.children
                      );
                    const textIndex =
                      siblings.indexOf(
                        text
                      );
                    for (
                      let i =
                        textIndex - 1;
                      i >= 0;
                      i -= 1
                    ) {
                      if (
                        siblings[
                          i
                        ].tagName.toLowerCase() ===
                        'rect'
                      ) {
                        rect =
                          siblings[
                          i
                          ] as SVGRectElement;
                        break;
                      }
                    }
                  }
                  if (!rect) {
                    return;
                  }
                  // ----------------------------------------------
                  // Get data point index
                  // ----------------------------------------------
                  const pointClass =
                    Array.from(
                      text.classList
                    ).find(
                      (className) =>
                        className.startsWith(
                          'chart-data-label-point-'
                        )
                    );
                  const dataPointIndex =
                    pointClass
                      ? Number(
                        pointClass.replace(
                          'chart-data-label-point-',
                          ''
                        )
                      )
                      : null;
                  // ----------------------------------------------
                  // Hide the whole annotation when the rendered
                  // bar segment is less than 10px high.
                  // ----------------------------------------------
                  if (
                    dataPointIndex !== null &&
                    Number.isFinite(
                      dataPointIndex
                    )
                  ) {
                    const seriesGroup =
                      container.querySelectorAll<SVGGElement>(
                        '.apexcharts-bar-series .apexcharts-series'
                      )[seriesIndex];
                    const bars =
                      seriesGroup?.querySelectorAll<SVGPathElement>(
                        '.apexcharts-bar-area'
                      );
                    const bar =
                      seriesGroup?.querySelector<SVGPathElement>(
                        `.apexcharts-bar-area[j="${dataPointIndex}"]`
                      ) ??
                      bars?.[dataPointIndex] ??
                      null;
                    if (bar) {
                      const barHeightAttribute =
                        bar.getAttribute(
                          'barHeight'
                        );
                      let segmentHeight =
                        barHeightAttribute !== null
                          ? Number(
                            barHeightAttribute
                          )
                          : Number.NaN;
                      if (
                        !Number.isFinite(
                          segmentHeight
                        )
                      ) {
                        try {
                          segmentHeight =
                            bar.getBBox().height;
                        } catch {
                          segmentHeight =
                            Number.POSITIVE_INFINITY;
                        }
                      }
                      const shouldHide =
                        Number.isFinite(
                          segmentHeight
                        ) &&
                        Math.abs(
                          segmentHeight
                        ) <
                        MIN_LABEL_SEGMENT_HEIGHT;
                      if (shouldHide) {
                        text.setAttribute(
                          'display',
                          'none'
                        );
                        rect.setAttribute(
                          'display',
                          'none'
                        );
                        return;
                      }
                      text.removeAttribute(
                        'display'
                      );
                      rect.removeAttribute(
                        'display'
                      );
                    }
                  }
                  // ----------------------------------------------
                  // Inline-flex design
                  // ----------------------------------------------
                  const PADDING_X =
                    8;
                  const PADDING_Y =
                    4;
                  const GAP = 4;
                  const DOT_RADIUS =
                    3;
                  const DOT_SIZE =
                    DOT_RADIUS * 2;
                  // ----------------------------------------------
                  // Original Apex dimensions
                  // ----------------------------------------------
                  const originalXAttribute =
                    rect.getAttribute(
                      'data-original-x'
                    );
                  const originalYAttribute =
                    rect.getAttribute(
                      'data-original-y'
                    );
                  const originalWidthAttribute =
                    rect.getAttribute(
                      'data-original-width'
                    );
                  const originalHeightAttribute =
                    rect.getAttribute(
                      'data-original-height'
                    );
                  const originalX =
                    originalXAttribute !==
                      null
                      ? Number(
                        originalXAttribute
                      )
                      : Number(
                        rect.getAttribute(
                          'x'
                        ) || 0
                      );
                  const originalY =
                    originalYAttribute !==
                      null
                      ? Number(
                        originalYAttribute
                      )
                      : Number(
                        rect.getAttribute(
                          'y'
                        ) || 0
                      );
                  const originalWidth =
                    originalWidthAttribute !==
                      null
                      ? Number(
                        originalWidthAttribute
                      )
                      : Number(
                        rect.getAttribute(
                          'width'
                        ) || 0
                      );
                  const originalHeight =
                    originalHeightAttribute !==
                      null
                      ? Number(
                        originalHeightAttribute
                      )
                      : Number(
                        rect.getAttribute(
                          'height'
                        ) || 0
                      );
                  if (
                    originalXAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-x',
                      String(
                        originalX
                      )
                    );
                  }
                  if (
                    originalYAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-y',
                      String(
                        originalY
                      )
                    );
                  }
                  if (
                    originalWidthAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-width',
                      String(
                        originalWidth
                      )
                    );
                  }
                  if (
                    originalHeightAttribute ===
                    null
                  ) {
                    rect.setAttribute(
                      'data-original-height',
                      String(
                        originalHeight
                      )
                    );
                  }
                  // ----------------------------------------------
                  // Center calculated by Apex
                  // ----------------------------------------------
                  const centerX =
                    originalX +
                    originalWidth / 2;
                  const centerY =
                    originalY +
                    originalHeight / 2;
                  // ----------------------------------------------
                  // Actual text dimensions
                  // ----------------------------------------------
                  let textBBox;
                  try {
                    textBBox =
                      text.getBBox();
                  } catch {
                    return;
                  }
                  const textWidth =
                    textBBox.width;
                  const textHeight =
                    textBBox.height;
                  // ----------------------------------------------
                  // Final chip dimensions
                  //
                  // 8 + dot 6 + gap 4 + text + 8
                  // ----------------------------------------------
                  const chipWidth =
                    PADDING_X +
                    DOT_SIZE +
                    GAP +
                    textWidth +
                    PADDING_X;
                  const chipHeight =
                    PADDING_Y +
                    textHeight +
                    PADDING_Y;
                  // ----------------------------------------------
                  // Keep the WHOLE chip centered
                  // ----------------------------------------------
                  const chipX =
                    centerX -
                    chipWidth / 2;
                  const chipY =
                    centerY -
                    chipHeight / 2;
                  rect.setAttribute(
                    'x',
                    String(chipX)
                  );
                  rect.setAttribute(
                    'y',
                    String(chipY)
                  );
                  rect.setAttribute(
                    'width',
                    String(
                      chipWidth
                    )
                  );
                  rect.setAttribute(
                    'height',
                    String(
                      chipHeight
                    )
                  );
                  rect.setAttribute(
                    'rx',
                    '4'
                  );
                  rect.setAttribute(
                    'ry',
                    '4'
                  );
                  // ----------------------------------------------
                  // Dot
                  // ----------------------------------------------
                  const dotCenterX =
                    chipX +
                    PADDING_X +
                    DOT_RADIUS;
                  const dotCenterY =
                    centerY;
                  // ----------------------------------------------
                  // Text after dot + gap
                  // ----------------------------------------------
                  const textStartX =
                    chipX +
                    PADDING_X +
                    DOT_SIZE +
                    GAP;
                  text.setAttribute(
                    'text-anchor',
                    'start'
                  );
                  text.setAttribute(
                    'x',
                    String(
                      textStartX
                    )
                  );
                  text.setAttribute(
                    'y',
                    String(
                      centerY
                    )
                  );
                  text.setAttribute(
                    'dominant-baseline',
                    'middle'
                  );
                  // ----------------------------------------------
                  // Create colored dot
                  // ----------------------------------------------
                  const circle =
                    document.createElementNS(
                      'http://www.w3.org/2000/svg',
                      'circle'
                    );
                  circle.setAttribute(
                    'class',
                    `chart-label-dot chart-label-dot-series-${seriesIndex}`
                  );
                  circle.setAttribute(
                    'cx',
                    String(
                      dotCenterX
                    )
                  );
                  circle.setAttribute(
                    'cy',
                    String(
                      dotCenterY
                    )
                  );
                  circle.setAttribute(
                    'r',
                    String(
                      DOT_RADIUS
                    )
                  );
                  circle.setAttribute(
                    'fill',
                    seriesColor
                  );
                  circle.setAttribute(
                    'stroke',
                    'none'
                  );
                  text.parentNode?.insertBefore(
                    circle,
                    text
                  );
                }
              );
            }
          );
        }
      );
    };
  // --------------------------------------------------------------------
  // Chart options
  // --------------------------------------------------------------------
  const chartOptions =
    useChart({
      chart: {
        stacked: true,
        type: 'bar',
        stackType: '100%',
        events: {
          mounted: () => {
            injectSeriesDotsIntoLabels();
          },
          animationEnd: () => {
            injectSeriesDotsIntoLabels();
          },
          updated: () => {
            injectSeriesDotsIntoLabels();
          },
          legendClick: (
            chartContext: any,
            seriesIndex: number,
            config: any
          ) => {
            const seriesName =
              config?.config
                ?.series?.[
                seriesIndex
              ]?.name;
            if (!seriesName) {
              return;
            }
            setHiddenSeries(
              (previous) => {
                const next =
                  new Set(
                    previous
                  );
                if (
                  next.has(
                    seriesName
                  )
                ) {
                  next.delete(
                    seriesName
                  );
                } else {
                  next.add(
                    seriesName
                  );
                }
                return next;
              }
            );
          },
        },
      },
      series,
      fill: {
        colors:
          resolvedColors,
      },
      colors:
        resolvedColors,
      labels:
        labels || [],
      xaxis: {
        labels: {
          offsetY: 4,
        },
      },
      grid: {
        padding: {
          top: 20,
          bottom: 8,
          left: 0,
        },
      },
      legend: {
        show:
          !hideLegend,
        itemMargin: {
          vertical: 8,
        },
        markers: {
          customHTML:
            function () {
              return `
                <div
                  style="
                    width: ${legendMarkerSize}px;
                    height: ${legendMarkerSize}px;
                    border-radius: 50%;
                    background-color: currentcolor;
                  "
                ></div>
              `;
            },
        },
        position: 'top',
        ...(legendLabelColor
          ? {
            labels: {
              colors:
                legendLabelColor,
            },
          }
          : {}),
      },
      /*
       * Native Apex labels disabled.
       */
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          barHeight:
            '80%',
          columnWidth:
            '70%',
          borderRadius:
            barBorderRadius,
          distributed:
            false,
          borderRadiusApplication:
            'end',
          borderRadiusWhenStacked:
            'last',
        },
      },
      stroke: {
        width: 0,
        show: false,
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (
            val: number
          ) =>
            `${val} %`,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({
          series,
          dataPointIndex,
          w,
        }) {
          if (
            !series ||
            !w.globals.seriesNames ||
            !w.globals.colors
          ) {
            return '';
          }
          const date =
            w.globals.labels[dataPointIndex] || '';
          const total =
            w.globals.stackedSeriesTotals[dataPointIndex];
          const collapsed = [
            ...(w.globals.collapsedSeriesIndices || []),
            ...(w.globals.ancillaryCollapsedSeriesIndices || []),
          ];
          const rows = series
            .map((s: any, i: number) => {
              if (collapsed.includes(i)) {
                return '';
              }
              const value = s[dataPointIndex];
              const percentage =
                total
                  ? ((value / total) * 100).toFixed(1)
                  : 0;
              const formattedValue =
                value != null && !isNaN(value)
                  ? `${formatNumber(value)} (${fPercent(percentage)})`
                  : '0';
              return `
          <div
            style="
              display:flex;
              align-items:center;
              width:100%;
              box-sizing:border-box;
              padding:4px;
              gap:8px;
            "
          >
            <span
              style="
                display:inline-block;
                width:4px;
                height:20px;
                border-radius:12px;
                background-color:${w.globals.colors[i]};
                flex-shrink:0;
              "
            ></span>
            <div
              style="
                flex:1;
                min-width:0;
                display:flex;
                justify-content:space-between;
                align-items:center;
                font-family:Public Sans,sans-serif;
                font-size:13px;
                font-weight:500;
                color:#333C47;
                white-space:nowrap;
                gap:16px;
              "
            >
              <span>
                ${w.globals.seriesNames[i]}
              </span>
              <span
                style="
                  flex-shrink:0;
                  text-align:right;
                "
              >
                ${formattedValue}
              </span>
            </div>
          </div>
        `;
            })
            .join('');
          const pointValues =
            series.map(
              (s: any) =>
                Number(s?.[dataPointIndex]) || 0
            );
          const pointNames =
            w.globals.seriesNames || [];
          const extraRows =
            extraTooltipRows &&
              extraTooltipRows.length
              ? extraTooltipRows
                .map((r) => {
                  const value =
                    r.compute(
                      pointValues,
                      pointNames
                    );
                  const formattedValue =
                    value != null && !isNaN(value)
                      ? formatNumber(value)
                      : '0';
                  return `
                <div
                  style="
                    display:flex;
                    align-items:center;
                    width:100%;
                    box-sizing:border-box;
                    padding:4px;
                    gap:8px;
                  "
                >
                  <span
                    style="
                      display:inline-block;
                      width:4px;
                      height:20px;
                      border-radius:12px;
                      background-color:${r.color};
                      flex-shrink:0;
                    "
                  ></span>
                  <div
                    style="
                      flex:1;
                      min-width:0;
                      display:flex;
                      justify-content:space-between;
                      align-items:center;
                      font-family:Public Sans,sans-serif;
                      font-size:13px;
                      font-weight:500;
                      color:#333C47;
                      white-space:nowrap;
                      gap:16px;
                    "
                  >
                    <span>
                      ${r.name}
                    </span>
                    <span
                      style="
                        flex-shrink:0;
                        text-align:right;
                      "
                    >
                      ${formattedValue}
                    </span>
                  </div>
                </div>
              `;
                })
                .join('')
              : '';
          return `
      <div
        style="
          width:320px;
          min-width:320px;
          max-width:320px;
          box-sizing:border-box;
          background:#FCFCFD;
          border:none;
          border-radius:12px;
          box-shadow:0px 4px 8px rgba(145,158,171,0.16);
          overflow:hidden;
        "
      >
        <div
          style="
            padding:12px 12px 4px 12px;
            box-sizing:border-box;
          "
        >
          <p
            style="
              font-family:Public Sans,sans-serif;
              color:#333C47;
              font-size:14px;
              font-weight:500;
              line-height:14px;
              white-space:nowrap;
              margin:0;
              margin-bottom:12px;
            "
          >
            ${date}
          </p>
        </div>
        <div
          style="
            padding:0 12px 12px 12px;
            display:flex;
            flex-direction:column;
            box-sizing:border-box;
          "
        >
          ${rows}
          ${extraRows}
        </div>
      </div>
    `;
        },
        y: {
          formatter: (
            val: number,
            {
              dataPointIndex,
              w,
            }
          ) => {
            const total =
              w.globals.stackedSeriesTotals[dataPointIndex];
            const percentage =
              total
                ? ((val / total) * 100).toFixed(2)
                : 0;
            return `${val.toFixed(0)} (${percentage}%)`;
          },
        },
      },
      annotations: {
        xaxis:
          annotations,
        points:
          labelAnnotations,
      },
    });
  const comparisonFooterHeight =
    Math.max(visibleSeriesCount, 1) * 16;
  return (
    <Box
      ref={chartContainerRef}
      sx={{
        width: '100%',
        'span .apexcharts-legend-marker': {
          borderRadius: 100,
        },
        '& .chart-data-label': {
          filter:
            'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04))',
        },
      }}
    >
      <Chart
        ref={chartRef}
        dir="ltr"
        type="bar"
        series={series}
        options={chartOptions}
        height={
          showComparison
            ? Math.max(
                height - comparisonFooterHeight,
                160
              )
            : height
        }
      />
      {showComparison && (
        <Box
          ref={comparisonContainerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: `${comparisonFooterHeight}px`,
            mt: '-2px',
          }}
        >
          {comparisonValues.map(
            (bucketValues, bucketIndex) => (
              <Box
                ref={(element: HTMLDivElement | null) => {
                  comparisonItemRefs.current[
                    bucketIndex
                  ] = element;
                }}
                key={`comparison-bucket-${bucketIndex}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: 'translateX(-50%)',
                  width: '76px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: '2px',
                }}
              >
                {bucketValues
                  .filter(
                    (metric) =>
                      !hiddenSeries.has(
                        metric.name
                      )
                  )
                  .map((metric) => {
                    const value = metric.value;
                    const textColor =
                      value === null ||
                      value === 0
                        ? '#919EAB'
                        : value > 0
                          ? '#22A06B'
                          : '#FF5630';
                    return (
                      <Box
                        key={`${bucketIndex}-${metric.name}`}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          minHeight: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        {bucketIndex === 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              right: '46px',
                              top: '50%',
                              transform:
                                'translateY(-50%)',
                              width: '8px',
                              height: '8px',
                              borderRadius:
                                '50%',
                              backgroundColor:
                                metric.color,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box
                          component="span"
                          sx={{
                            width: '42px',
                            textAlign: 'right',
                            fontVariantNumeric:
                              'lining-nums tabular-nums ordinal',
                            fontFamily:
                              'Public Sans',
                            fontSize: '12px',
                            color: textColor,
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '120%',
                          }}
                        >
                          {formatComparisonValue(
                            value
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )
          )}
        </Box>
      )}
    </Box>
  );
}